import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Image,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableWithoutFeedback,
} from 'react-native';

import { devicePixelRatio } from './devicePixelRatio';
import { measureImage } from './measureImage';
import { doubleTapSize, paneContainSize } from './sharpContainSize';

import type { LayoutChangeEvent } from 'react-native';

interface ImageRendererProps {
  fileUrl?: string;
  thumbnailUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  fileName: string;
  /**
   * When `onToggleSharpPixels` is set, the parent owns the mode. A carousel
   * list can remount the cell after layout, and an internal flag would snap
   * back to the fitted size. On, the picture shows `doubleTapSize`.
   */
  showSharpPixels?: boolean;
  onToggleSharpPixels?: () => void;
}

export const ImageRenderer: React.FC<ImageRendererProps> = ({
  fileUrl,
  thumbnailUrl,
  isLoading,
  hasError,
  fileName,
  showSharpPixels: showSharpPixelsProp = false,
  onToggleSharpPixels,
}) => {
  const lastTapRef = useRef(0);
  const [internalSharp, setInternalSharp] = useState(false);
  const controlled = Boolean(onToggleSharpPixels);
  const showSharpPixels = controlled ? showSharpPixelsProp : internalSharp;
  const [frame, setFrame] = useState({ width: 0, height: 0 });
  const [natural, setNatural] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [measureFailed, setMeasureFailed] = useState(false);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      if (onToggleSharpPixels) {
        onToggleSharpPixels();
      } else {
        setInternalSharp(value => !value);
      }
    }
    lastTapRef.current = now;
  }, [onToggleSharpPixels]);

  const displayUri = fileUrl ?? thumbnailUrl;
  const pane =
    frame.width > 0 && frame.height > 0
      ? frame
      : {
          width: typeof window !== 'undefined' ? window.innerWidth : 0,
          height:
            typeof window !== 'undefined'
              ? Math.max(window.innerHeight - 120, 1)
              : 0,
        };
  const ratio = devicePixelRatio();
  const opened =
    natural && pane.width > 0 && pane.height > 0
      ? paneContainSize(natural, pane, ratio)
      : null;
  const enlarged =
    natural && pane.width > 0 && pane.height > 0
      ? doubleTapSize(natural, pane, ratio)
      : null;
  const fitted = showSharpPixels ? enlarged : opened;
  const onFrameLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setFrame(prev =>
      prev.width === width && prev.height === height ? prev : { width, height },
    );
  }, []);

  useEffect(() => {
    if (!displayUri || hasError) {
      setNatural(null);
      setMeasureFailed(false);
      return;
    }
    let cancelled = false;
    if (!controlled) {
      setInternalSharp(false);
    }
    setNatural(null);
    setMeasureFailed(false);
    measureImage(
      displayUri,
      (width, height) => {
        if (cancelled) {
          return;
        }
        if (width > 0 && height > 0) {
          setNatural({ width, height });
        } else {
          setMeasureFailed(true);
        }
      },
      () => {
        if (!cancelled) {
          setMeasureFailed(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [controlled, displayUri, hasError]);

  if (hasError) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>Failed to load image</Text>
        <Text style={styles.errorSub}>{fileName}</Text>
      </View>
    );
  }

  return (
    <View
      style={styles.container}
      onLayout={onFrameLayout}
      testID="carousel-image-frame"
    >
      {displayUri ? (
        <TouchableWithoutFeedback
          onPress={handleDoubleTap}
          accessibilityLabel={fileName}
        >
          <View style={styles.imageWrap}>
            {fitted && fitted.width > 0 && fitted.height > 0 ? (
              <Image
                source={{ uri: displayUri }}
                style={{ width: fitted.width, height: fitted.height }}
                resizeMode="contain"
                testID="carousel-image"
              />
            ) : measureFailed ? (
              <Image
                source={{ uri: displayUri }}
                style={styles.imageFill}
                resizeMode="contain"
                testID="carousel-image"
              />
            ) : (
              <ActivityIndicator size="large" color="#fff" />
            )}
          </View>
        </TouchableWithoutFeedback>
      ) : null}
      {isLoading && (
        <View style={styles.loadingOverlay}>
          {thumbnailUrl ? (
            <Image
              source={{ uri: thumbnailUrl }}
              style={[styles.imageFill, styles.blurredThumb]}
              resizeMode="contain"
            />
          ) : null}
          <ActivityIndicator size="large" color="#fff" style={styles.spinner} />
        </View>
      )}
      {!displayUri && !isLoading && !hasError && (
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#fff" />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageWrap: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  imageFill: {
    width: '100%',
    height: '100%',
  },
  blurredThumb: {
    opacity: 0.4,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  spinner: {
    position: 'absolute',
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 4,
  },
  errorSub: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
  },
});
